import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import GroupedColumnPickerPanel, { ColumnPickerGroup, useCloseOnOutsideClick } from './GroupedColumnPickerPanel';
import { getCategoryTagStyle } from './categoryTagStyles';
import { useTheme } from '../../../../../theme/useTheme';
import TablePager from './TablePager';
import { CrewingHorizontalScrollView, CrewingVerticalScrollView } from './webScrollStyles';
import type { LocationTableTotals } from '../crewingAnalyticsApi';

export interface LocationLevelEfficiencyRow {
  region: string;
  market: string;
  location: string;
  // Backend Location_ID — required by /efficiency/employee-table's `locations` filter; the
  // display `location` name never matches any Schedule row there.
  locationId?: string;
  year: string;
  period: string;
  week: string;
  category: string;
  zone: string;
  forecastedCases: number;
  actualCases: number;
  forecastedHours: number;
  scheduleHours: number;
  estimatedHours: number;
  actualHours: number;
  siteStandardKcph: number;
  scheduledKcph: number;
  actualKcph: number;
  targetKcph: number;
  kcphIndex: number;
}

interface LocationLevelEfficiencyTableProps {
  title: string;
  rows: LocationLevelEfficiencyRow[];
  selectedColumnKeys?: ColumnKey[];
  /** Called with the newly-selected dimension keys so the caller can re-fetch server-side grouped rows. */
  onColumnsChange?: (dimensionKeys: string[]) => void;
  /** Called with the clicked location's (locationId, display name) so the caller can drill into its Employee Level Efficiency table. */
  onLocationSelect?: (locationId: string, locationName: string) => void;
  /** Aggregate metrics across the FULL filtered result set (backend `totals`) — render this
   * directly for the grand-total row, never recompute it from the current page's `rows`. */
  totals?: LocationTableTotals | null;
  /** Full filtered grouped-row count (backend `total`), used by the pager and footnote. */
  total?: number;
  /** Zero-based current page. */
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  /** True while a page request is in flight. */
  pageLoading?: boolean;
  /** Selectable rows-per-page values — the picker is hidden if this or `onPageSizeChange` is omitted. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  /** Server-side sort state (backend `sortBy`/`sortDirection`) — rows arrive pre-sorted, so this
   * table no longer sorts client-side. `sortBy` is null/undefined for the default grouping order. */
  sortBy?: string | null;
  sortDirection?: 'asc' | 'desc';
  /** Called with the clicked column's key — caller re-fetches page 0 with the new sort applied. */
  onSortChange?: (columnKey: string) => void;
}

const formatNum = (value: number) => value.toLocaleString();


const CELL_WIDE = 132;
const CELL_STD = 72;
const CELL_MED = 100;
const CELL_NUM = 116;

type ColumnKey = keyof LocationLevelEfficiencyRow;

interface ColumnDef {
  key: ColumnKey;
  label: string;
  width: 'wide' | 'std' | 'med' | 'num';
  numeric?: boolean;
  link?: boolean;
  /** KCPH-style rate columns show a single average instead of the total/avg pair used for cases and hours. */
  rate?: boolean;
  percent?: boolean;
}

const COLUMN_DEFS: ColumnDef[] = [
  { key: 'region', label: 'Region / Unit', width: 'wide' },
  { key: 'market', label: 'Market / POD', width: 'wide' },
  { key: 'location', label: 'Location', width: 'wide', link: true },
  { key: 'year', label: 'Year', width: 'std' },
  { key: 'period', label: 'Period', width: 'std' },
  { key: 'week', label: 'Week', width: 'std' },
  { key: 'category', label: 'Category', width: 'med' },
  { key: 'zone', label: 'Zone', width: 'med' },
  { key: 'forecastedCases', label: 'Forecasted Cases', width: 'num', numeric: true },
  { key: 'actualCases', label: 'Actual Cases', width: 'num', numeric: true },
  { key: 'forecastedHours', label: 'Forecasted Hours', width: 'num', numeric: true },
  { key: 'scheduleHours', label: 'Schedule Hours', width: 'num', numeric: true },
  { key: 'estimatedHours', label: 'Estimated Hours', width: 'num', numeric: true },
  { key: 'actualHours', label: 'Actual Hours', width: 'num', numeric: true },
  { key: 'siteStandardKcph', label: 'Site Standard KCPH', width: 'num', numeric: true, rate: true },
  { key: 'scheduledKcph', label: 'Scheduled KCPH', width: 'num', numeric: true, rate: true },
  { key: 'actualKcph', label: 'Actual KCPH', width: 'num', numeric: true, rate: true },
  { key: 'targetKcph', label: 'Target KCPH', width: 'num', numeric: true, rate: true },
  { key: 'kcphIndex', label: 'KCPH Index %', width: 'num', numeric: true, rate: true, percent: true },
];

// Only these (groupable) columns are toggleable in the picker — metrics always display.
const DIMENSION_KEYS: ColumnKey[] = ['region', 'market', 'location', 'year', 'period', 'week', 'category', 'zone'];
const METRIC_DEFS = COLUMN_DEFS.filter((col) => !DIMENSION_KEYS.includes(col.key));

const COLUMN_GROUPS: ColumnPickerGroup[] = [
  {
    key: 'location-hierarchy',
    label: 'Location Hierarchy',
    options: [
      { key: 'region', label: 'Region / Unit' },
      { key: 'market', label: 'Market / POD' },
      { key: 'location', label: 'Location' },
    ],
  },
  {
    key: 'timeframe',
    label: 'Timeframe',
    options: [
      { key: 'year', label: 'Year' },
      { key: 'period', label: 'Period' },
      { key: 'week', label: 'Week' },
    ],
  },
  {
    key: 'other-attributes',
    label: 'Other Attributes',
    options: [
      { key: 'category', label: 'Category' },
      { key: 'zone', label: 'Zone' },
    ],
  },
];

// Allowed server-side sortBy keys per the backend contract — dimension columns plus the metric
// columns, excluding location-attribute-only columns (size/routes/siteSqft) which aren't sortable.
const SORTABLE_KEYS: ColumnKey[] = [
  ...DIMENSION_KEYS,
  'forecastedCases', 'actualCases', 'forecastedHours', 'scheduleHours', 'estimatedHours',
  'actualHours', 'siteStandardKcph', 'scheduledKcph', 'actualKcph', 'targetKcph', 'kcphIndex',
];

const LocationLevelEfficiencyTable: React.FC<LocationLevelEfficiencyTableProps> = ({ title, rows, selectedColumnKeys: controlledSelectedColumnKeys, onColumnsChange, onLocationSelect, totals, total, page = 0, pageSize = 25, onPageChange, pageLoading, pageSizeOptions, onPageSizeChange, sortBy, sortDirection = 'asc', onSortChange }) => {
  const isDark = useTheme().resolvedTheme === 'dark';

  const [isColumnPickerOpen, setIsColumnPickerOpen] = useState(false);
  const columnPickerWrapRef = useRef<View>(null);
  useCloseOnOutsideClick(columnPickerWrapRef, () => setIsColumnPickerOpen(false), isColumnPickerOpen);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<ColumnKey[]>(controlledSelectedColumnKeys ?? DIMENSION_KEYS);

  useEffect(() => {
    if (controlledSelectedColumnKeys) {
      setSelectedColumnKeys(controlledSelectedColumnKeys);
    }
  }, [controlledSelectedColumnKeys]);

  const handleColumnsChange = (keys: ColumnKey[]) => {
    setSelectedColumnKeys(keys);
    onColumnsChange?.(keys);
  };

  const selectedColumns = useMemo(
    () => [
      ...COLUMN_DEFS.filter((col) => DIMENSION_KEYS.includes(col.key) && selectedColumnKeys.includes(col.key)),
      ...METRIC_DEFS,
    ],
    [selectedColumnKeys],
  );

  const isSortable = (key: ColumnKey) => SORTABLE_KEYS.includes(key) && !!onSortChange;

  const tableMinWidth = useMemo(
    () =>
      selectedColumns.reduce(
        (sum, col) => sum + (col.width === 'wide' ? CELL_WIDE : col.width === 'num' ? CELL_NUM : col.width === 'med' ? CELL_MED : CELL_STD),
        0,
      ),
    [selectedColumns],
  );

  const getCellWidthStyle = (width: ColumnDef['width']) => {
    if (width === 'wide') return styles.cellWide;
    if (width === 'num') return styles.cellNum;
    if (width === 'med') return styles.cellMed;
    return null;
  };


  return (
    <View style={styles.card}>
      <View style={styles.headerTopRow}>
        <Text style={styles.title}>{title}</Text>
        <View style={styles.columnPickerWrap} ref={columnPickerWrapRef}>
          <TouchableOpacity
            style={styles.columnsPill}
            onPress={() => setIsColumnPickerOpen((prev) => !prev)}
          >
            <Text style={styles.columnsPillText}>{`Columns (${selectedColumnKeys.length})`}</Text>
          </TouchableOpacity>

          {isColumnPickerOpen && (
            <GroupedColumnPickerPanel
              groups={COLUMN_GROUPS}
              selectedKeys={selectedColumnKeys}
              onChange={(keys) => handleColumnsChange(keys as ColumnKey[])}
            />
          )}
        </View>
      </View>
      <CrewingHorizontalScrollView
        horizontal
        showsHorizontalScrollIndicator
        contentContainerStyle={styles.scrollContent}
      >
        <View style={[styles.tableInner, { minWidth: tableMinWidth }]}>
          <View style={styles.headerRow}>
            {selectedColumns.map((column) => {
              const isActive = sortBy === column.key;
              const sortable = isSortable(column.key);
              return (
                <TouchableOpacity
                  key={column.key}
                  style={[styles.headerCell, getCellWidthStyle(column.width)]}
                  onPress={sortable ? () => onSortChange!(column.key) : undefined}
                  disabled={!sortable}
                  activeOpacity={sortable ? 0.7 : 1}
                >
                  <Text style={[styles.headerCellText, isActive && styles.headerCellTextActive]} numberOfLines={2}>
                    {column.label}
                  </Text>
                  {sortable && (
                    <MaterialIcons
                      name={isActive ? (sortDirection === 'asc' ? 'arrow-drop-up' : 'arrow-drop-down') : 'unfold-more'}
                      size={14}
                      color={isActive ? '#034895' : '#9CA3AF'}
                    />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>

          <View style={styles.summaryRow}>
            {selectedColumns.map((column, index) => {
              let value = '--';
              if (column.key === 'region') value = 'Total';
              if (column.numeric) {
                const key = column.key as keyof LocationTableTotals;
                const raw = totals ? totals[key] : null;
                value = raw == null
                  ? '--'
                  : column.rate
                    ? `${Number(raw).toFixed(1)}${column.percent ? '%' : ''}`
                    : formatNum(Number(raw));
              }
              return (
                <Text key={`summary-${column.key}-${index}`} style={[styles.summaryCell, getCellWidthStyle(column.width)]}>
                  {value}
                </Text>
              );
            })}
          </View>

          <View style={styles.tableViewport}>
            <CrewingVerticalScrollView nestedScrollEnabled showsVerticalScrollIndicator>
              {rows.map((row, index) => (
                <View key={`${row.location}-${row.week}-${index}`} style={styles.dataRow}>
                  {selectedColumns.map((column) => {
                    const rawValue = row[column.key];
                    const isPlaceholderText = (column.key === 'region' || column.key === 'market') && (rawValue === '0' || rawValue === 0 || !rawValue);
                    const displayValue = column.numeric
                      ? column.rate
                        ? `${Number(rawValue ?? 0).toFixed(1)}${column.percent ? '%' : ''}`
                        : formatNum(Number(rawValue ?? 0))
                      : isPlaceholderText
                        ? '--'
                        : String(rawValue ?? '--');

                    if (column.key === 'category' && displayValue !== '--') {
                      const tagStyle = getCategoryTagStyle(displayValue, isDark);
                      return (
                        <View key={`${row.location}-${index}-${column.key}`} style={[getCellWidthStyle(column.width), styles.categoryCell]}>
                          <View style={[styles.categoryTag, { backgroundColor: tagStyle.backgroundColor }]}>
                            <Text style={[styles.categoryTagText, { color: tagStyle.color }]} numberOfLines={1}>
                              {displayValue}
                            </Text>
                          </View>
                        </View>
                      );
                    }

                    if (column.key === 'location' && column.link && displayValue !== '--' && onLocationSelect) {
                      return (
                        <TouchableOpacity
                          key={`${row.location}-${index}-${column.key}`}
                          style={getCellWidthStyle(column.width)}
                          onPress={() => onLocationSelect(row.locationId ?? displayValue, displayValue)}
                        >
                          <Text style={styles.linkText} numberOfLines={1} ellipsizeMode="tail">
                            {displayValue}
                          </Text>
                        </TouchableOpacity>
                      );
                    }

                    return (
                      <Text
                        key={`${row.location}-${index}-${column.key}`}
                        style={[
                          styles.dataCell,
                          getCellWidthStyle(column.width),
                          column.link ? styles.linkText : null,
                        ]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                      >
                        {displayValue}
                      </Text>
                    );
                  })}
                </View>
              ))}
            </CrewingVerticalScrollView>
          </View>
        </View>
      </CrewingHorizontalScrollView>
      <Text style={styles.footnote}>
        {`${(total ?? rows.length).toLocaleString()} groups total. Cases and hours sum; KCPH measures average. This table is the efficiency lens: volume, hours, and productivity against site standards. Crewing quality percentages live on the Crewing Execution tab.`}
      </Text>
      {onPageChange && (
        <TablePager page={page} pageSize={pageSize} total={total ?? rows.length} onPageChange={onPageChange} loading={pageLoading} pageSizeOptions={pageSizeOptions} onPageSizeChange={onPageSizeChange} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,    borderColor: '#E5E7EB',
    padding: 14,
    overflow: 'visible',
  },
  title: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  footnote: {
    marginTop: 10,
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
    lineHeight: 17,
  },
  headerTopRow: {
    marginBottom: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    position: 'relative',
    zIndex: 30,
  },
  columnPickerWrap: {
    position: 'relative',
    zIndex: 40,
  },
  columnsPill: {
    backgroundColor: '#EFF6FF',
    borderWidth: 1,
    borderColor: '#BFDBFE',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  columnsPillText: {
    fontSize: 11,
    color: '#0B5FC6',
    fontWeight: '600',
  },
  scrollContent: {
    flexGrow: 1,
  },
  tableInner: {
    width: '100%',
  },
  tableViewport: {
    flex: 1,
    minHeight: 260,
    maxHeight: 420,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    paddingVertical: 8,
    backgroundColor: '#FFFFFF',
  },
  dataRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#D7E5F8',
    paddingVertical: 8,
  },
  summaryRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#D0D0D0',
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    backgroundColor: '#F3F7FD',
    paddingVertical: 9,
  },
  headerCell: {
    flexGrow: 1,
    flexBasis: CELL_STD,
    minWidth: CELL_STD,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 2,
    paddingHorizontal: 6,
  },
  headerCellText: {
    flex: 1,
    fontSize: 10,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#616161',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  headerCellTextActive: {
    color: '#034895',
  },
  dataCell: {
    flexGrow: 1,
    flexBasis: CELL_STD,
    minWidth: CELL_STD,
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#3A3A3A',
    paddingHorizontal: 6,
  },
  summaryCell: {
    flexGrow: 1,
    flexBasis: CELL_STD,
    minWidth: CELL_STD,
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: '#2D2D2D',
    fontWeight: '700',
    paddingHorizontal: 6,
  },
  cellWide: {
    flexGrow: 1,
    flexBasis: CELL_WIDE,
    minWidth: CELL_WIDE,
  },
  cellMed: {
    flexGrow: 1,
    flexBasis: CELL_MED,
    minWidth: CELL_MED,
  },
  cellNum: {
    flexGrow: 1,
    flexBasis: CELL_NUM,
    minWidth: CELL_NUM,
    textAlign: 'right',
  },
  linkText: {
    color: '#0B5FC6',
    fontWeight: '600',
  },
  categoryCell: {
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  categoryTag: {
    alignSelf: 'flex-start',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  categoryTagText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
});

export default LocationLevelEfficiencyTable;
